<template>
  <div>
    <vx-card class="mb-4">
      <div class="flex justify-between items-start flex-wrap gap-2">
        <div>
          <h4 class="mb-1">{{ $t('withdrawal.title') }}</h4>
          <small class="text-muted">{{ $t('withdrawal.hint') }}</small>
        </div>
        <div class="flex items-center gap-2">
          <b-form-select v-model="filterStatus" :options="statusFilters" size="sm" style="width: 220px" @change="load"/>
          <b-button size="sm" variant="outline-primary" @click="load">
            <feather-icon icon="RefreshCwIcon" class="mr-25"/>{{ $t('action.reload') }}
          </b-button>
        </div>
      </div>
    </vx-card>

    <vx-card>
      <div v-if="isLoading" class="text-center py-4 text-muted">{{ $t('common.loading') }}</div>

      <div v-else-if="!rows.length" class="text-center py-4">
        <feather-icon icon="CheckCircleIcon" class="h-8 w-8 text-success mb-1"/>
        <p class="text-muted mb-0">{{ $t('withdrawal.empty') }}</p>
      </div>

      <table v-else class="w-full admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ $t('withdrawal.influencer') }}</th>
            <th class="text-right">{{ $t('withdrawal.amount') }}</th>
            <th>{{ $t('withdrawal.pix_key') }}</th>
            <th>{{ $t('withdrawal.scheduled_for') }}</th>
            <th>{{ $t('status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="w in rows" :key="w.id">
            <td>{{ w.id }}</td>
            <td>
              {{ _.get(w, 'influencer.name') }}
              <small class="text-muted d-block">{{ $t('withdrawal.requested_at') }}: {{ formatDate(w.requested_at) }}</small>
            </td>
            <td class="text-right">
              <strong>{{ money(w.amount_cents) }}</strong>
              <small v-if="w.tax_withheld_cents" class="text-muted d-block">
                {{ $t('withdrawal.net') }}: {{ money(w.net_cents) }}
              </small>
            </td>
            <td>
              <code>{{ w.pix_key }}</code>
              <small class="text-muted d-block">{{ pixTypeLabel(w.pix_key_type) }}</small>
            </td>
            <td>
              <span v-if="w.scheduled_for">{{ formatDate(w.scheduled_for) }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <vs-chip transparent :color="statusColor(w.status)">
                {{ $t(`withdrawal.status_.${w.status}`) }}
              </vs-chip>
              <small v-if="w.external_reference" class="text-muted d-block">E2E {{ w.external_reference }}</small>
              <small v-if="w.rejection_reason || w.failure_reason" class="text-danger d-block">
                {{ w.rejection_reason || w.failure_reason }}
              </small>
            </td>
            <td class="text-right actions">
              <b-button
                v-permission="'withdrawals.approve'"
                v-if="w.status === 'SO'"
                size="sm" variant="success" @click="act(w, 'approve')">
                {{ $t('withdrawal.approve') }}
              </b-button>
              <b-button
                v-permission="'withdrawals.schedule'"
                v-if="['SO','AP'].includes(w.status)"
                size="sm" variant="outline-primary" @click="openSchedule(w)">
                {{ $t('withdrawal.schedule') }}
              </b-button>
              <b-button
                v-permission="'withdrawals.pay'"
                v-if="['AP','AG','PR'].includes(w.status)"
                size="sm" variant="primary" @click="openPay(w)">
                {{ $t('withdrawal.pay') }}
              </b-button>
              <b-button
                v-permission="'withdrawals.approve'"
                v-if="['SO','AP','AG'].includes(w.status)"
                size="sm" variant="outline-danger" @click="act(w, 'reject')">
                {{ $t('withdrawal.reject') }}
              </b-button>
              <b-button
                v-permission="'withdrawals.pay'"
                v-if="['AP','AG','PR'].includes(w.status)"
                size="sm" variant="outline-warning" @click="act(w, 'fail')">
                {{ $t('withdrawal.fail') }}
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vx-card>

    <b-modal
      v-model="scheduleModal"
      :title="$t('withdrawal.schedule')"
      :ok-title="$t('save')"
      :cancel-title="$t('action.cancel')"
      @ok="confirmSchedule">
      <b-form-group :label="$t('withdrawal.scheduled_for')">
        <b-form-input v-model="scheduleDate" type="date"/>
        <small class="text-muted">{{ $t('withdrawal.schedule_hint') }}</small>
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="payModal"
      :title="$t('withdrawal.pay')"
      :ok-title="$t('withdrawal.confirm_payment')"
      :cancel-title="$t('action.cancel')"
      ok-variant="primary"
      @ok="confirmPay">
      <b-form-group :label="$t('withdrawal.e2e')">
        <b-form-input v-model="payReference" type="text" placeholder="E2E..."/>
        <small class="text-muted">{{ $t('withdrawal.e2e_hint') }}</small>
      </b-form-group>
      <b-form-group :label="$t('withdrawal.tax_withheld')">
        <b-input-group prepend="R$">
          <b-form-input v-model="payTax" type="number" step="0.01" min="0"/>
        </b-input-group>
        <small class="text-muted">{{ $t('withdrawal.tax_hint') }}</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BFormGroup, BFormInput, BFormSelect, BInputGroup
} from 'bootstrap-vue'
import WalletAdminService from '@/services/api/WalletAdminService'

export default {
  components: { BButton, BModal, BFormGroup, BFormInput, BFormSelect, BInputGroup },
  data: () => ({
    service: null,
    rows: [],
    isLoading: false,
    // Padrão são os estados em aberto: é a fila de trabalho, não o histórico.
    filterStatus: 'open',
    scheduleModal: false,
    scheduleDate: null,
    payModal: false,
    payReference: '',
    payTax: null,
    current: null
  }),
  computed: {
    statusFilters() {
      return [
        { text: this.$t('withdrawal.filter_open'), value: 'open' },
        { text: this.$t('withdrawal.filter_paid'), value: 'PG' },
        { text: this.$t('withdrawal.filter_closed'), value: 'closed' },
        { text: this.$t('withdrawal.filter_all'), value: 'all' }
      ]
    }
  },
  methods: {
    money(cents) {
      return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatDate(v) { return v ? new Date(v).toLocaleDateString('pt-BR') : '' },
    pixTypeLabel(type) {
      return { CP: 'CPF', CN: 'CNPJ', EM: 'E-mail', TE: 'Telefone', AL: 'Aleatória' }[type] || type
    },
    statusColor(status) {
      switch (status) {
        case 'PG': return 'success'
        case 'RJ': case 'FA': return 'danger'
        case 'CA': return 'dark'
        default: return 'warning'
      }
    },
    /** 'all' manda lista vazia, e o backend só filtra quando recebe status. */
    selectedStatuses() {
      if (this.filterStatus === 'open') return ['SO', 'AP', 'AG', 'PR']
      if (this.filterStatus === 'closed') return ['RJ', 'CA', 'FA']
      if (this.filterStatus === 'all') return []
      return [this.filterStatus]
    },
    load() {
      this.isLoading = true
      this.service.withdrawals(this.selectedStatuses()).then(
        r => { this.rows = r.data || [] },
        () => { this.rows = [] }
      ).finally(() => { this.isLoading = false })
    },
    act(withdrawal, action) {
      const needsReason = ['reject', 'fail'].includes(action)
      const reason = needsReason ? window.prompt(this.$t('withdrawal.reason_prompt')) : ''
      if (needsReason && reason === null) return

      const call = {
        approve: () => this.service.approveWithdrawal(withdrawal.id),
        reject: () => this.service.rejectWithdrawal(withdrawal.id, reason || ''),
        fail: () => this.service.failWithdrawal(withdrawal.id, reason || '')
      }[action]

      this.$vs.loading()
      call().then(
        () => { this.notifySuccess(this.$vs, this.$t('messages.success')); this.load() },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    openSchedule(withdrawal) {
      this.current = withdrawal
      this.scheduleDate = null
      this.scheduleModal = true
    },
    confirmSchedule() {
      if (!this.current || !this.scheduleDate) return
      this.$vs.loading()
      this.service.scheduleWithdrawal(this.current.id, this.scheduleDate).then(
        () => { this.notifySuccess(this.$vs, this.$t('messages.success')); this.load() },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    openPay(withdrawal) {
      this.current = withdrawal
      this.payReference = ''
      this.payTax = null
      this.payModal = true
    },
    confirmPay() {
      if (!this.current) return
      const tax = this.payTax ? Math.round(Number(this.payTax) * 100) : null
      this.$vs.loading()
      this.service.payWithdrawal(this.current.id, this.payReference, tax).then(
        () => { this.notifySuccess(this.$vs, this.$t('messages.success')); this.load() },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    }
  },
  beforeMount() { this.service = WalletAdminService.build(this.$vs) },
  mounted() { this.load() }
}
</script>

<style scoped>
.admin-table th { font-size: 0.75rem; opacity: 0.7; text-align: left; padding-bottom: 0.35rem; }
.admin-table td { padding: 0.6rem 0.4rem; border-top: 1px solid rgba(0,0,0,0.06); vertical-align: top; }
.text-right { text-align: right; }
.actions { white-space: nowrap; }
.actions .btn { margin-left: 0.25rem; }
</style>
